import { type JSX, useCallback, useEffect, useState } from 'react';
import {
  type AtendimentoDetalhe,
  type Capacidades,
  type ConversaAtiva,
  type PassoDaConversa,
  api,
} from '../lib/api.js';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Spoiler,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { EstadoNoDetalhe } from './EstadoDoBot.js';
import { NomeDoAluno } from './NomeDoAluno.js';
import { PreviaWhatsapp } from './PreviaWhatsapp.js';

/**
 * Conversa reconstruída de `atendimento_evento`.
 *
 * Dois cuidados que a tela tem de carregar, porque o dado por si não os diz:
 *
 * 1. Em modo sombra NADA chegou ao aluno. As bolhas mostram o que ele
 *    receberia — e se isso não estiver gritado no topo, a tela é indistinguível
 *    de um histórico real.
 * 2. Atendimento anterior ao registro do texto enviado não tem o literal: ali
 *    o que aparece é a resposta ATUAL da FAQ, com aviso no topo e rótulo na
 *    bolha. Depois de uma edição, o aluno viu outro texto.
 */

const ROTULO_TIPO: Record<string, string> = {
  navegacao: 'Navegação',
  faq_hit: 'Respondeu',
  faq_miss: 'Não respondeu',
  bloqueio_pagamento: 'Bloqueio de pagamento',
  handoff: 'Transbordo',
  envio: 'Enviado ao aluno',
  shadow: 'Simulação (sombra)',
  veredito_sombra: 'Veredito da secretaria',
};

const ROTULO_FAIXA: Record<string, string> = {
  direto: 'faixa alta — respondeu sem LLM',
  llm: 'faixa média — classificador escolheu',
  lacuna: 'abaixo do limite — virou lacuna',
};

const ROTULO_MOTIVO: Record<string, string> = {
  sem_candidatos: 'a busca não trouxe candidato',
  score_baixo: 'cosseno abaixo do limite inferior',
  llm_nenhuma: 'o classificador respondeu NENHUMA',
  ma_resposta: 'o aluno marcou 👎',
  baixa_confianca: 'baixa confiança',
  solicitado: 'o aluno pediu atendente',
  humano_assumiu: 'um humano assumiu a conversa',
};

function hora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR');
}

function duracao(segundos: number | null): string {
  if (segundos === null) return 'em aberto';
  if (segundos < 60) return `${segundos}s`;
  return `${Math.floor(segundos / 60)}min ${String(segundos % 60).padStart(2, '0')}s`;
}

export function DetalheAtendimento({
  atendimento,
  capacidades,
  aoFechar,
  aoVerConversa,
}: {
  atendimento: AtendimentoDetalhe;
  capacidades: Capacidades;
  aoFechar: () => void;
  /**
   * Abre o fio contínuo deste telefone. Esta tela continua sendo a de
   * AUDITORIA — o link leva à outra leitura do mesmo dado, não substitui esta.
   */
  aoVerConversa?: (atendimentoId: string) => void;
}): JSX.Element {
  /**
   * Estado ATUAL da sessão deste JID.
   *
   * É onde a pessoa já está quando percebe que o bot não respondeu — obrigar a
   * ir para outra aba seria trabalho inventado. Lido do Redis a cada abertura,
   * porque o estado tem TTL e o detalhe pode ficar aberto.
   */
  const [conversa, setConversa] = useState<ConversaAtiva | null>(null);
  const [erroRedis, setErroRedis] = useState(false);

  const lerEstado = useCallback(async () => {
    try {
      const r = await api.get<{ estado: ConversaAtiva | null }>(
        `/admin/conversas/estado?atendimento=${atendimento.id}`,
      );
      setConversa(r.estado);
      setErroRedis(false);
    } catch {
      setErroRedis(true);
    }
  }, [atendimento.id]);

  useEffect(() => {
    void lerEstado();
  }, [lerEstado]);

  const segundos = atendimento.fechadoEm
    ? Math.round(
        (new Date(atendimento.fechadoEm).getTime() - new Date(atendimento.abertoEm).getTime()) /
          1000,
      )
    : null;

  return (
    <Card withBorder mt="lg" padding="md">
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <Title order={2} size="h4">
              {atendimento.protocolo}
            </Title>
            <Badge color={atendimento.modo === 'shadow' ? 'orange' : 'teal'}>
              {atendimento.modo}
            </Badge>
          </Group>
          <Group gap="xs" wrap="nowrap">
            {aoVerConversa && (
              <Button variant="light" onClick={() => aoVerConversa(atendimento.id)}>
                Ver conversa completa
              </Button>
            )}
            <Button onClick={aoFechar}>Fechar</Button>
          </Group>
        </Group>

        {/* Sombra: a tela inteira é uma simulação. */}
        {atendimento.nadaFoiEnviado && (
          <Alert color="orange" variant="light" title="Modo sombra: NADA foi enviado a este aluno">
            O bot processou a mensagem, decidiu a resposta e enviou o relatório ao grupo interno.
            As bolhas abaixo são o que o aluno <em>receberia</em> se o modo fosse live.
          </Alert>
        )}

        {!atendimento.nadaFoiEnviado && !atendimento.temRegistroDeEnvio && (
          <Alert color="gray" variant="light">
            Atendimento <strong>anterior ao registro do texto enviado</strong>. Aqui o painel
            mostra a resposta <em>atual</em> de cada FAQ, não o que saiu na época — se a resposta
            foi editada depois, o aluno viu outra versão. Atendimentos novos têm o texto literal
            gravado.
          </Alert>
        )}

        <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="sm">
          <Ficha rotulo="Aluno">
            <NomeDoAluno nome={atendimento.nome} origem={atendimento.nomeOrigem} />
            {/* Os dois quando divergem: é contexto útil para quem vai atender. */}
            {atendimento.nomeOrigem === 'contato' &&
              atendimento.pushName &&
              atendimento.pushName !== atendimento.nome && (
                <Text size="xs" c="dimmed">
                  no WhatsApp: {atendimento.pushName}
                </Text>
              )}
          </Ficha>
          <Ficha rotulo="Telefone">
            <Text size="sm" ff="monospace">
              {atendimento.telefone ?? '—'}
            </Text>
            {!atendimento.telefoneCompleto && (
              <Text size="xs" c="dimmed">
                mascarado para o seu papel
              </Text>
            )}
          </Ficha>
          <Ficha rotulo="Tipo / perfil">
            <Text size="sm">
              {atendimento.tipoContato ?? '—'}
              {atendimento.perfil ? ` · ${atendimento.perfil}` : ''}
            </Text>
          </Ficha>
          <Ficha rotulo="Abertura">
            <Text size="sm">{hora(atendimento.abertoEm)}</Text>
          </Ficha>
          <Ficha rotulo="Duração">
            <Text size="sm">{duracao(segundos)}</Text>
          </Ficha>
          <Ficha rotulo="Desfecho">
            <Text size="sm">{atendimento.desfecho}</Text>
          </Ficha>
        </SimpleGrid>

        <Divider />

        <Title order={3} size="h6">
          Estado do bot nesta conversa
        </Title>
        <EstadoNoDetalhe
          conversa={conversa}
          capacidades={capacidades}
          erroRedis={erroRedis}
          aoMudar={() => void lerEstado()}
        />

        <Title order={3} size="h6" mt="xs">
          Conversa ({atendimento.passos.length} passo(s))
        </Title>
        {atendimento.passos.length === 0 && (
          <Text size="sm" c="dimmed">
            Nenhum evento gravado para este atendimento.
          </Text>
        )}
        <Stack gap="xs">
          {atendimento.passos.map((passo, i) => (
            <Passo
              key={passo.id}
              passo={passo}
              sombra={atendimento.nadaFoiEnviado}
              entradaAnterior={atendimento.passos[i - 1]?.entrada ?? null}
            />
          ))}
        </Stack>

        <Title order={3} size="h6" mt="xs">
          Transbordos ({atendimento.handoffs.length})
        </Title>
        {atendimento.handoffs.length === 0 ? (
          <Text size="sm" c="dimmed">
            O bot não passou esta conversa para ninguém.
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={520}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Motivo</Table.Th>
                  <Table.Th>Início</Table.Th>
                  <Table.Th>Fim</Table.Th>
                  <Table.Th>Encerrado por</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {atendimento.handoffs.map((hl) => (
                  <Table.Tr key={hl.id}>
                    <Table.Td>{ROTULO_MOTIVO[hl.motivo] ?? hl.motivo}</Table.Td>
                    <Table.Td>{hora(hl.iniciadoEm)}</Table.Td>
                    <Table.Td>{hl.encerradoEm ? hora(hl.encerradoEm) : 'em aberto'}</Table.Td>
                    <Table.Td>{hl.encerradoPor ?? '—'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Card>
  );
}

/** Rótulo + valor da ficha do atendimento. */
function Ficha({ rotulo, children }: { rotulo: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase">
        {rotulo}
      </Text>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Passo({
  passo,
  sombra,
  entradaAnterior,
}: {
  passo: PassoDaConversa;
  sombra: boolean;
  entradaAnterior: string | null;
}): JSX.Element {
  // O evento `envio` repete a entrada da rodada, que o passo de decisão já
  // mostrou. Repetir a bolha do aluno faria parecer que ele digitou duas
  // vezes — mas se o passo anterior tinha outra entrada (ou não havia passo),
  // a bolha aparece: é a única chance de ver o que foi digitado.
  const mostrarEntrada = passo.entrada !== null && passo.entrada !== entradaAnterior;
  const scores = [
    passo.cosseno !== null ? `cos ${passo.cosseno.toFixed(4)}` : null,
    passo.cossenoDoTopo !== null && passo.cossenoDoTopo !== passo.cosseno
      ? `topo ${passo.cossenoDoTopo.toFixed(4)}`
      : null,
    passo.melhorCosseno !== null && passo.melhorCosseno !== passo.cossenoDoTopo
      ? `máx ${passo.melhorCosseno.toFixed(4)}`
      : null,
  ].filter(Boolean);

  /**
   * Cor da borda por tipo de passo — com o RÓTULO do tipo sempre ao lado. A
   * cor agrupa visualmente; quem informa é o texto.
   */
  const cor =
    passo.tipo === 'faq_hit' || passo.tipo === 'envio'
      ? 'teal'
      : passo.tipo === 'faq_miss'
        ? 'red'
        : passo.tipo === 'bloqueio_pagamento'
          ? 'orange'
          : passo.tipo === 'handoff'
            ? 'blue'
            : 'gray';

  return (
    <Paper withBorder p="xs" style={{ borderLeftWidth: 3, borderLeftColor: `var(--mantine-color-${cor}-filled)` }}>
      <Stack gap={6}>
        <Group gap="sm" wrap="wrap">
          <Text size="sm" fw={600}>
            {ROTULO_TIPO[passo.tipo] ?? passo.tipo}
          </Text>
          <Text size="xs" c="dimmed">
            {hora(passo.em)}
          </Text>
          {passo.deNo && (
            <Text size="xs" c="dimmed" ff="monospace">
              {passo.deNo}
              {passo.paraNo && passo.paraNo !== passo.deNo ? ` → ${passo.paraNo}` : ''}
            </Text>
          )}
          {passo.feedback && (
            <Badge color={passo.feedback === 'positivo' ? 'teal' : 'red'} size="xs">
              {passo.feedback === 'positivo' ? '👍 positivo' : '👎 negativo'}
            </Badge>
          )}
        </Group>

        {mostrarEntrada && (
          <Paper withBorder p="xs" radius="md" maw={440} ml="auto">
            <Text size="sm">{passo.entrada}</Text>
          </Paper>
        )}

        {(passo.faixa || passo.via || scores.length > 0 || passo.motivo) && (
          <Text size="xs" c="dimmed">
            {passo.faixa && <>{ROTULO_FAIXA[passo.faixa]} · </>}
            {passo.via && <>via {passo.via} · </>}
            {passo.motivo && <>{ROTULO_MOTIVO[passo.motivo] ?? passo.motivo} · </>}
            {scores.join(' · ')}
          </Text>
        )}

        {passo.opcaoEscolhida && (
          <Text size="sm">
            <strong>Pediu:</strong> {passo.opcaoEscolhida}
          </Text>
        )}

        {passo.faqPergunta && (
          <Text size="xs" c="dimmed">
            FAQ: {passo.faqPergunta}
            {passo.faqCategoria ? ` (${passo.faqCategoria})` : ''}
          </Text>
        )}

        {passo.mensagens.map((texto, i) => (
          <PreviaWhatsapp
            key={`${passo.id}-${i}`}
            texto={texto}
            nota={
              sombra
                ? 'O aluno receberia este texto. Nada foi enviado.'
                : passo.respostaEhAtual
                  ? 'Texto ATUAL da FAQ — atendimento anterior ao registro de envio. Se a resposta foi editada depois, o aluno viu outra versão.'
                  : 'Texto literal que saiu ao aluno.'
            }
          />
        ))}

        {passo.candidatos.length > 0 && (
          <Spoiler
            maxHeight={0}
            showLabel={`${passo.candidatos.length} candidato(s) considerado(s)`}
            hideLabel="esconder candidatos"
            fz="xs"
          >
            <Box component="ol" pl="md" m={0}>
              {passo.candidatos.map((c, i) => (
                <li key={`${passo.id}-c-${i}`}>
                  <Text size="xs" component="span">
                    <Badge size="xs" mr={4}>
                      {c.rotulo ?? i + 1}
                    </Badge>
                    {c.pergunta ?? c.trecho ?? c.faqId}{' '}
                    <Text span c="dimmed" size="xs">
                      {c.cosseno !== undefined && `cos ${c.cosseno.toFixed(4)}`}
                      {c.rrf !== undefined && ` · rrf ${c.rrf.toFixed(4)}`}
                    </Text>
                  </Text>
                </li>
              ))}
            </Box>
          </Spoiler>
        )}

        {passo.trace.length > 0 && (
          <Spoiler maxHeight={0} showLabel="Rastro da decisão" hideLabel="esconder rastro" fz="xs">
            <Box component="ul" pl="md" m={0}>
              {passo.trace.map((linha, i) => (
                <li key={`${passo.id}-t-${i}`}>
                  <Text size="xs">{linha}</Text>
                </li>
              ))}
            </Box>
          </Spoiler>
        )}
      </Stack>
    </Paper>
  );
}
